import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, ExternalLink, Search } from "lucide-react";
import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from "recharts";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { Sparkline } from "@/components/cinch/Sparkline";
import { TierChip } from "@/components/cinch/TierChip";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import {
  monthLabel,
  monthLabelShort,
  useRetentionDetail,
  type KpiMonthRange,
  type KpiRetentionDetail,
  type RetentionEntity,
  type RetentionOutcome,
  type TierBasis,
} from "../api";
import type { KpiSearch } from "..";
import type { KpiPeriod } from "./PeriodSelector";
import { fmtArr, fmtUsd0 } from "../format";

const OUTCOMES: RetentionOutcome[] = ["expanded", "flat", "contracted", "churned"];

const OUTCOME_META: Record<
  RetentionOutcome,
  { label: string; variant: BadgeProps["variant"]; tone: "positive" | "muted" | "danger" }
> = {
  expanded: { label: "Expanded", variant: "success", tone: "positive" },
  flat: { label: "Flat", variant: "neutral", tone: "muted" },
  contracted: { label: "Contracted", variant: "warning", tone: "danger" },
  churned: { label: "Churned", variant: "danger", tone: "danger" },
};

const TIER_BASIS_LABEL: Record<TierBasis, string> = {
  base: "12 mo ago",
  current: "Current",
  initial: "Lifecycle start",
};

function tierOf(e: RetentionEntity, basis: TierBasis): number | null {
  return basis === "current" ? e.tier_current : basis === "initial" ? e.tier_initial : e.tier_base;
}

export function RetentionDrawer({
  period,
  range,
}: {
  period: KpiPeriod;
  range: KpiMonthRange;
}) {
  const navigate = useNavigate();
  const search = useSearch({ from: "/kpi" });
  const open = search.tile === "retention";
  const expanded = !!search.expanded;

  const go = (next: Partial<KpiSearch>) =>
    navigate({ to: "/kpi", search: (prev: KpiSearch) => ({ ...prev, ...next }) });

  // Server-side slice (drives totals + chart + rows).
  const [tierBasis, setTierBasis] = useState<TierBasis>("base");
  const [tiers, setTiers] = useState<number[]>([]);
  const detail = useRetentionDetail(period, range, tierBasis, tiers, open);

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(v) => {
        if (!v && !expanded) go({ tile: undefined, expanded: undefined });
      }}
      expanded={expanded}
      onExpandedChange={(exp) => open && go({ expanded: exp ? true : undefined })}
      onClose={() => {
        if (expanded) go({ expanded: undefined });
        else go({ tile: undefined, expanded: undefined });
      }}
      title="$ Retention — NRR / GDR"
      subtitle={retentionSubtitle(detail.data?.anchor_month)}
    >
      <RetentionDetailBody
        detail={detail}
        expanded={expanded}
        tierBasis={tierBasis}
        setTierBasis={setTierBasis}
        tiers={tiers}
        setTiers={setTiers}
      />
    </EntityDrawer>
  );
}

/** Standard drawer subtitle for any retention drill-down (KPI card or the
 *  Customer Tiers → Retention tab), so the copy stays in one place. */
export function retentionSubtitle(anchorMonth?: string): string {
  return anchorMonth
    ? `YoY as of ${monthLabel(anchorMonth)} · vs 12 mo prior`
    : "Revenue waterfall · entity-rolled";
}

/** The drawer body — loading / empty / quick / expanded switch. Extracted so
 *  the Customer Tiers → Retention tab can reuse the exact same detail view
 *  inside its own EntityDrawer shell. */
export function RetentionDetailBody({
  detail,
  expanded,
  tierBasis,
  setTierBasis,
  tiers,
  setTiers,
}: {
  detail: { isLoading: boolean; data?: KpiRetentionDetail };
  expanded: boolean;
  tierBasis: TierBasis;
  setTierBasis: (b: TierBasis) => void;
  tiers: number[];
  setTiers: (t: number[]) => void;
}) {
  if (detail.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading cohort…</div>;
  }
  if (!detail.data?.available) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        No retention data for this period.
      </div>
    );
  }
  return expanded ? (
    <ExpandedView
      data={detail.data}
      tierBasis={tierBasis}
      setTierBasis={setTierBasis}
      tiers={tiers}
      setTiers={setTiers}
    />
  ) : (
    <QuickView data={detail.data} tierBasis={tierBasis} />
  );
}

// ─── shared bits ──────────────────────────────────────────────────────

function tone(pct: number | null | undefined): string {
  if (pct == null) return "text-muted-foreground";
  if (pct >= 100) return "text-success";
  if (pct >= 85) return "text-purple";
  return "text-danger";
}

function TotalsHeader({ data }: { data: KpiRetentionDetail }) {
  const t = data.totals;
  return (
    <div className="border-b border-border p-4">
      <div className="flex items-end gap-6">
        <Stat label="NRR" value={t?.nrr_pct} big />
        <Stat label="GDR" value={t?.gdr_pct} />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
        <MiniStat label="Cohort ARR · 12 mo prior" value={fmtUsd0(t?.base_arr)} />
        <MiniStat label="$-for-$ retained (GDR)" value={fmtUsd0(t?.retained_arr)} />
        <MiniStat label="Billed now (NRR)" value={fmtUsd0(t?.current_arr)} />
      </div>
      <div className="mt-2 text-[11px] text-muted-foreground">
        {t?.base_count ?? 0} economic entities in base
      </div>
    </div>
  );
}

function Stat({ label, value, big }: { label: string; value: number | null | undefined; big?: boolean }) {
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
        {label}
      </div>
      <div
        className={cn(
          "font-extrabold leading-none tracking-tight tabular-nums",
          big ? "text-3xl" : "text-xl",
          tone(value),
        )}
      >
        {value != null ? `${value.toFixed(1)}%` : "—"}
      </div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">
        {label}
      </div>
      <div className="mt-0.5 font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function OutcomeChip({ outcome }: { outcome: RetentionOutcome }) {
  const m = OUTCOME_META[outcome];
  return <Badge variant={m.variant}>{m.label}</Badge>;
}

/** Aggregate entities into per-outcome count + net Δ$. */
function outcomeBreakdown(entities: RetentionEntity[]) {
  return OUTCOMES.map((o) => {
    const rows = entities.filter((e) => e.outcome === o);
    return {
      outcome: o,
      count: rows.length,
      delta: rows.reduce((s, e) => s + e.delta_arr, 0),
    };
  });
}

// ─── quick view ───────────────────────────────────────────────────────

function QuickView({ data, tierBasis }: { data: KpiRetentionDetail; tierBasis: TierBasis }) {
  const entities = data.entities ?? [];
  const breakdown = outcomeBreakdown(entities);
  const movers = [...entities]
    .sort((a, b) => Math.abs(b.delta_arr) - Math.abs(a.delta_arr))
    .slice(0, 8);

  return (
    <div>
      <TotalsHeader data={data} />

      <div className="p-4">
        <SectionTitle>By outcome</SectionTitle>
        <div className="mt-2 flex flex-col gap-1.5">
          {breakdown.map((b) => (
            <div key={b.outcome} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2">
                <OutcomeChip outcome={b.outcome} />
                <span className="text-muted-foreground">{b.count}</span>
              </span>
              <DeltaText value={b.delta} />
            </div>
          ))}
        </div>
      </div>

      <div className="px-4 pb-4">
        <SectionTitle>Top movers</SectionTitle>
        <div className="mt-2 flex flex-col divide-y divide-border">
          {movers.map((e) => (
            <div key={e.entity_id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{e.name}</div>
                <div className="text-[11px] text-muted-foreground tabular-nums">
                  {fmtArr(e.base_arr, 0)} → {fmtArr(e.current_arr, 0)}
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                <TierChip tier={tierOf(e, tierBasis)} />
                <DeltaText value={e.delta_arr} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <Methodology />
    </div>
  );
}

/** How the numbers are built — shown at the foot of the quick view so the
 *  definitions travel with the data (for a human, and for an AI reading it). */
function Methodology() {
  return (
    <div className="border-t border-border bg-muted/30 p-4">
      <SectionTitle>How this is calculated</SectionTitle>
      <div className="mt-2 space-y-2 text-[11px] leading-relaxed text-muted-foreground">
        <p>
          Each account is compared to itself a year ago — <b>current</b> billing
          vs <b>prior-year</b> billing (the same month, 12 months earlier) from
          the revenue waterfall.
        </p>
        <div className="rounded-md border border-border bg-background px-2.5 py-2 font-mono text-[10.5px] text-foreground">
          <div>NRR = Σ current ÷ Σ prior-year</div>
          <div className="mt-1">GDR = Σ min(current, prior-year) ÷ Σ prior-year</div>
        </div>
        <p>
          <b className="text-foreground">NRR</b> is uncapped — expansion pushes it
          above 100%. <b className="text-foreground">GDR</b> floors each account
          at its own prior-year amount, so expansion can&apos;t mask a loss and it
          never exceeds 100%.
        </p>
        <p>
          <b className="text-foreground">Merged &amp; split accounts</b> are rolled
          up to one economic entity before the math, so revenue moving between
          linked accounts counts as <b>retained</b>, not churn — the floor is
          applied once, at the entity level.
        </p>
        <p>
          <b className="text-foreground">New logos</b> billed within the last 12
          months have no prior-year figure, so there&apos;s nothing to retain —
          they&apos;re excluded from the base entirely. Retention only measures
          dollars that existed a year ago.
        </p>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      {children}
    </div>
  );
}

function DeltaText({ value }: { value: number }) {
  if (Math.abs(value) < 1) return <span className="text-muted-foreground text-xs">—</span>;
  const positive = value > 0;
  return (
    <span
      className={cn(
        "text-xs font-semibold tabular-nums",
        positive ? "text-success" : "text-danger",
      )}
    >
      {positive ? "▲" : "▼"} {fmtArr(Math.abs(value), 0)}
    </span>
  );
}

// ─── expanded view ────────────────────────────────────────────────────

function ExpandedView({
  data,
  tierBasis,
  setTierBasis,
  tiers,
  setTiers,
}: {
  data: KpiRetentionDetail;
  tierBasis: TierBasis;
  setTierBasis: (b: TierBasis) => void;
  tiers: number[];
  setTiers: (t: number[]) => void;
}) {
  // Client-side refinements over the server-filtered rows.
  const [outcomes, setOutcomes] = useState<Set<RetentionOutcome>>(new Set());
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<"loss" | "gain" | "base">("loss");

  const rows = useMemo(() => {
    let out = data.entities ?? [];
    if (outcomes.size) out = out.filter((e) => outcomes.has(e.outcome));
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      out = out.filter((e) => e.name.toLowerCase().includes(q));
    }
    const sorted = [...out];
    if (sort === "loss") sorted.sort((a, b) => a.delta_arr - b.delta_arr);
    else if (sort === "gain") sorted.sort((a, b) => b.delta_arr - a.delta_arr);
    else sorted.sort((a, b) => b.base_arr - a.base_arr);
    return sorted;
  }, [data.entities, outcomes, query, sort]);

  const toggleOutcome = (o: RetentionOutcome) => {
    const next = new Set(outcomes);
    next.has(o) ? next.delete(o) : next.add(o);
    setOutcomes(next);
  };
  const toggleTier = (t: number) =>
    setTiers(tiers.includes(t) ? tiers.filter((x) => x !== t) : [...tiers, t]);

  return (
    <div className="flex flex-col">
      <TotalsHeader data={data} />

      {/* Chart */}
      <div className="border-b border-border p-4">
        <SectionTitle>NRR &amp; GDR · trailing 12 months</SectionTitle>
        <NrrGdrChart series={data.series ?? []} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-border p-4">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Tier basis
          </span>
          <div className="flex overflow-hidden rounded-md border border-border">
            {(["base", "current", "initial"] as TierBasis[]).map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => setTierBasis(b)}
                className={cn(
                  "px-2.5 py-1 text-xs font-medium",
                  tierBasis === b
                    ? "bg-primary text-primary-foreground"
                    : "bg-background text-muted-foreground hover:bg-accent",
                )}
              >
                {TIER_BASIS_LABEL[b]}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Tiers
          </span>
          {[1, 2, 3, 4, 5].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => toggleTier(t)}
              className={cn(
                "transition",
                tiers.length && !tiers.includes(t) && "opacity-35",
              )}
              aria-pressed={tiers.includes(t)}
            >
              <TierChip tier={t} />
            </button>
          ))}
          {tiers.length > 0 && (
            <button
              type="button"
              onClick={() => setTiers([])}
              className="text-[11px] text-muted-foreground underline hover:text-foreground"
            >
              clear
            </button>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {OUTCOMES.map((o) => (
            <button key={o} type="button" onClick={() => toggleOutcome(o)}>
              <Badge
                variant={outcomes.has(o) ? OUTCOME_META[o].variant : "neutral"}
                className={cn(!outcomes.size ? "" : !outcomes.has(o) && "opacity-40")}
              >
                {OUTCOME_META[o].label}
              </Badge>
            </button>
          ))}
        </div>

        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search account…"
            className="h-8 w-48 pl-7 text-xs"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-background text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr className="border-b border-border">
              <th className="w-6" />
              <th className="px-3 py-2 text-left font-semibold">Account</th>
              <th className="px-3 py-2 text-left font-semibold">Tier</th>
              <th className="px-3 py-2 text-right font-semibold">Base · 12mo</th>
              <th className="px-3 py-2 text-right font-semibold">Current</th>
              <SortableTh label="Δ ARR" active={sort} onCycle={setSort} />
              <th className="px-3 py-2 text-left font-semibold">Outcome</th>
              <th className="px-3 py-2 text-left font-semibold">Trend</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <EntityRow key={e.entity_id} entity={e} tierBasis={tierBasis} />
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  No accounts match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="p-4 text-[10px] text-muted-foreground">
        {rows.length} of {data.totals?.base_count ?? 0} entities · GDR floors each entity at 100%
        so a merge/split shows as retained, not churn.
      </div>
    </div>
  );
}

function SortableTh({
  label,
  active,
  onCycle,
}: {
  label: string;
  active: "loss" | "gain" | "base";
  onCycle: (s: "loss" | "gain" | "base") => void;
}) {
  const arrow = active === "loss" ? "↑" : active === "gain" ? "↓" : "";
  return (
    <th className="px-3 py-2 text-right font-semibold">
      <button
        type="button"
        onClick={() => onCycle(active === "loss" ? "gain" : "loss")}
        className="inline-flex items-center gap-1 hover:text-foreground"
        title="Sort by Δ ARR"
      >
        {label} <span className="text-[9px]">{arrow}</span>
      </button>
    </th>
  );
}

function EntityRow({ entity, tierBasis }: { entity: RetentionEntity; tierBasis: TierBasis }) {
  const [open, setOpen] = useState(false);
  const traj = entity.trajectory.map((t) => t.current_arr);
  const sparkTone =
    entity.outcome === "churned" || entity.outcome === "contracted"
      ? "danger"
      : entity.outcome === "expanded"
        ? "positive"
        : "muted";

  return (
    <>
      <tr
        className="cursor-pointer border-b border-border/60 hover:bg-accent/40"
        onClick={() => setOpen((v) => !v)}
      >
        <td className="pl-2 text-muted-foreground">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </td>
        <td className="px-3 py-2">
          <span className="font-medium">{entity.name}</span>
          {entity.members.length > 1 && (
            <span className="ml-1.5 text-[10px] text-muted-foreground">
              +{entity.members.length - 1} linked
            </span>
          )}
        </td>
        <td className="px-3 py-2">
          <TierChip tier={tierOf(entity, tierBasis)} />
        </td>
        <td className="px-3 py-2 text-right tabular-nums">{fmtUsd0(entity.base_arr)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{fmtUsd0(entity.current_arr)}</td>
        <td className="px-3 py-2 text-right">
          <span
            className={cn(
              "tabular-nums font-semibold",
              entity.delta_arr > 0 ? "text-success" : entity.delta_arr < 0 ? "text-danger" : "text-muted-foreground",
            )}
          >
            {entity.delta_arr > 0 ? "+" : ""}
            {fmtUsd0(entity.delta_arr)}
            <span className="ml-1 text-[10px] font-normal text-muted-foreground">
              {entity.delta_pct > 0 ? "+" : ""}
              {entity.delta_pct.toFixed(0)}%
            </span>
          </span>
        </td>
        <td className="px-3 py-2">
          <OutcomeChip outcome={entity.outcome} />
        </td>
        <td className="px-3 py-2">
          <div className="w-24">
            <Sparkline points={traj} tone={sparkTone} height={22} />
          </div>
        </td>
      </tr>
      {open && (
        <tr className="border-b border-border bg-muted/30">
          <td />
          <td colSpan={7} className="px-3 py-3">
            <div className="flex flex-wrap items-start gap-6">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Current-ARR trajectory
                </div>
                <div className="mt-1 w-64">
                  <Sparkline points={traj} tone={sparkTone} height={40} />
                </div>
                <div className="mt-1 flex justify-between text-[10px] text-muted-foreground tabular-nums">
                  <span>{entity.trajectory[0] ? monthLabelShort(entity.trajectory[0].month) : ""}</span>
                  <span>
                    {entity.trajectory.at(-1) ? monthLabelShort(entity.trajectory.at(-1)!.month) : ""}
                  </span>
                </div>
              </div>

              <div className="min-w-[12rem]">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Economic entity ({entity.members.length})
                </div>
                <ul className="mt-1 space-y-0.5 text-xs">
                  {entity.members.map((m) => (
                    <li key={m.qb_id} className="flex items-center gap-1.5">
                      <span className="text-muted-foreground">QB-{m.qb_id}</span>
                      <span className="truncate">{m.name}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid grid-cols-3 gap-x-5 gap-y-1 text-xs">
                <MiniStat label="Retained (GDR)" value={fmtUsd0(entity.retained_arr)} />
                <TierReadings entity={entity} />
              </div>

              <Link
                to="/renewals/pipeline"
                search={{ account: entity.entity_id, expanded: true }}
                className="ml-auto inline-flex items-center gap-1 self-center text-xs font-medium text-brand hover:underline"
              >
                Open renewal record <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function TierReadings({ entity }: { entity: RetentionEntity }) {
  return (
    <>
      <div>
        <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">
          12 mo ago
        </div>
        <TierChip tier={entity.tier_base} className="mt-0.5" />
      </div>
      <div>
        <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">
          Current
        </div>
        <TierChip tier={entity.tier_current} className="mt-0.5" />
      </div>
      <div>
        <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">
          Start
        </div>
        <TierChip tier={entity.tier_initial} className="mt-0.5" />
      </div>
    </>
  );
}

// ─── chart ────────────────────────────────────────────────────────────

function NrrGdrChart({ series }: { series: NonNullable<KpiRetentionDetail["series"]> }) {
  const data = series.map((s) => ({
    month: monthLabelShort(s.month),
    nrr: s.nrr_pct,
    gdr: s.gdr_pct,
  }));
  return (
    <div className="mt-2 h-48">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
          <XAxis
            dataKey="month"
            tickLine={false}
            axisLine={false}
            tickMargin={6}
            tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={38}
            domain={["auto", "auto"]}
            tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
            tickFormatter={(v: number) => `${v}%`}
          />
          <Tooltip content={<ChartTooltip />} />
          <Line
            type="monotone"
            dataKey="nrr"
            name="NRR"
            stroke="hsl(var(--cinch-power))"
            strokeWidth={2}
            dot={{ r: 2 }}
            isAnimationActive={false}
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="gdr"
            name="GDR"
            stroke="hsl(var(--status-purple))"
            strokeWidth={2}
            dot={{ r: 2 }}
            isAnimationActive={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="mt-1 flex items-center justify-center gap-4 text-[10px]">
        <LegendDot className="bg-power" label="NRR" />
        <LegendDot className="bg-purple" label="GDR" />
      </div>
    </div>
  );
}

function LegendDot({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-muted-foreground">
      <span className={cn("h-2 w-2 rounded-full", className)} />
      {label}
    </span>
  );
}

function ChartTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-popover px-2 py-1 text-[10px] shadow-md">
      <div className="font-semibold text-popover-foreground">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="tabular-nums" style={{ color: p.color }}>
          {p.name}: {p.value != null ? `${Number(p.value).toFixed(1)}%` : "—"}
        </div>
      ))}
    </div>
  );
}
